import React, { useState } from "react";
import { Grid, Checkbox, TextField, Typography, Switch, FormControlLabel, Autocomplete, Box } from "@mui/material";
import { BuyerSellerFormProps, TransactingPartyIndividual } from "../types";

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const BuyerSellerForm: React.FC<BuyerSellerFormProps> = ({
  individual,
  setIndividual,
  organization,
  setOrganization,
  joint,
  setJoint,
  sameAsPropertyAddress,
  setSameAsPropertyAddress,
  address,
  setAddress,
  isJoint,
  setIsJoint,
  contactType,
  isOrganization,
  setIsOrganization
}) => {
  const vestings = [
    ",a corporation",
    ",a Georgia limited liability company",
    ",a limited liability company",
    ",a Limited Liability Partnership",
    ",a partnership",
    ",as community property",
    ",as her sole and separate property",
    ",as his sole and separate property",
    ",as joint tenants",
    ",as sole and separate property",
    ",as tenants by the entirety",
    ",as tenants in common",
    "Purchaser with contractual rights under a purchase agreement...",
    "Trust"
  ];

  const organizationTypes = [
    "Corporation",
    "Limited Liability Corp.",
    "Limited Liability Company",
    "Limited Partnership",
    "Partnership",
    "Trust",
    "Estate",
    "Other"
  ];

  const [isValid, setIsValid] = useState(true);

  const handleSetJoint = (prop1: "jointOne" | "jointTwo", prop2: keyof TransactingPartyIndividual, value: string) => {
    setJoint({
      ...joint,
      [prop1]: {
        ...joint[prop1],
        [prop2]: value
      }
    });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {contactType}
      </Typography>

      <Grid container spacing={3} alignItems="center">
        {/* Toggle: Individual / Org */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography
              variant="body1"
              sx={{ cursor: "pointer", fontWeight: !isOrganization ? "bold" : "normal" }}
              onClick={() => setIsOrganization(false)}
            >
              Individual
            </Typography>
            <Typography
              variant="body1"
              sx={{ cursor: "pointer", fontWeight: isOrganization ? "bold" : "normal" }}
              onClick={() => {
                setIsOrganization(true);
                setIsJoint(false);
              }}
            >
              Organization
            </Typography>
          </Box>
        </Grid>

        {/* Joint switch */}
        <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          {!isOrganization && (
            <FormControlLabel
              control={<Switch checked={isJoint} onChange={() => setIsJoint(!isJoint)} />}
              label="Is Joint"
            />
          )}
        </Grid>

        {/* === JOINT MODE === */}
        {isJoint && (
          <>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                value={joint.relationshipType}
                options={vestings}
                onChange={(e, value) => setJoint({ ...joint, relationshipType: value || "" })}
                renderInput={(params) => <TextField {...params} label="Joint Relationship" />}
              />
            </Grid>

            {/* Joint #1 */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Joint #1</Typography>
              <TextField
                label="First Name"
                fullWidth
                value={joint.jointOne.firstName}
                onChange={(e) => handleSetJoint("jointOne", "firstName", e.target.value)}
              />
              <TextField
                label="Middle Name"
                fullWidth
                value={joint.jointOne.middleName}
                onChange={(e) => handleSetJoint("jointOne", "middleName", e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={joint.jointOne.lastName}
                onChange={(e) => handleSetJoint("jointOne", "lastName", e.target.value)}
                sx={{ mt: 2 }}
              />
            </Grid>

            {/* Joint #2 */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Joint #2</Typography>
              <TextField
                label="First Name"
                fullWidth
                value={joint.jointTwo.firstName}
                onChange={(e) => handleSetJoint("jointTwo", "firstName", e.target.value)}
              />
              <TextField
                label="Middle Name"
                fullWidth
                value={joint.jointTwo.middleName}
                onChange={(e) => handleSetJoint("jointTwo", "middleName", e.target.value)}
                sx={{ mt: 2 }}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={joint.jointTwo.lastName}
                onChange={(e) => handleSetJoint("jointTwo", "lastName", e.target.value)}
                sx={{ mt: 2 }}
              />
            </Grid>
          </>
        )}

        {/* === ORGANIZATION MODE === */}
        {!isJoint && isOrganization && (
          <>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={organization.isPayeeNameSameAsName}
                    onChange={(e) => setOrganization({ ...organization, isPayeeNameSameAsName: e.target.checked })}
                  />
                }
                label="Payee same as name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                value={organization.name}
                onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={organizationTypes}
                value={organization.organizationType}
                onChange={(e, value) => setOrganization({ ...organization, organizationType: value || "" })}
                renderInput={(params) => <TextField {...params} label="Organization Type" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payee Name"
                fullWidth
                value={organization.isPayeeNameSameAsName ? organization.name : organization.payeeName}
                onChange={(e) => setOrganization({ ...organization, payeeName: e.target.value })}
                disabled={organization.isPayeeNameSameAsName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={vestings}
                value={organization.vesting}
                onChange={(e, value) => setOrganization({ ...organization, vesting: value || "" })}
                renderInput={(params) => <TextField {...params} label="Vested In" />}
              />
            </Grid>
          </>
        )}

        {/* === INDIVIDUAL MODE === */}
        {!isJoint && !isOrganization && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                value={individual.firstName}
                onChange={(e) => setIndividual({ ...individual, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Middle Name"
                fullWidth
                value={individual.middleName}
                onChange={(e) => setIndividual({ ...individual, middleName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                fullWidth
                value={individual.lastName}
                onChange={(e) => setIndividual({ ...individual, lastName: e.target.value })}
              />
            </Grid>
          </>
        )}

        {/* === Address Section === */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsPropertyAddress}
                onChange={() => setSameAsPropertyAddress(!sameAsPropertyAddress)}
              />
            }
            label="Address same as property"
          />
        </Grid>

        {!sameAsPropertyAddress && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address"
                fullWidth
                value={address.address1}
                onChange={(e) => setAddress({ ...address, address1: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address 2"
                fullWidth
                value={address.address2}
                onChange={(e) => setAddress({ ...address, address2: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="State"
                fullWidth
                value={address.state}
                onChange={(e) => setAddress({ ...address, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Zip"
                fullWidth
                value={address.zip}
                onChange={(e) => setAddress({ ...address, zip: e.target.value })}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone"
            fullWidth
            type="tel"
            value={address.phone}
            onChange={(e) => setAddress({ ...address, phone: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            fullWidth
            type="email"
            value={address.email}
            error={!isValid}
            helperText={!isValid && "Invalid email"}
            onFocus={() => setIsValid(true)}
            onChange={(e) => {
              setIsValid(true);
              setAddress({ ...address, email: e.target.value });
            }}
            onBlur={(e) => {
              setIsValid(validateEmail(e.target.value));
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BuyerSellerForm;
